import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Sparkles, ChevronRight } from "lucide-react";
import { COLLECTIONS } from "@/lib/collections";
import { VIDEOS } from "@/lib/videos";
import VideoBlock from "@/components/VideoBlock";

const PILLARS = [
    { n: "01", t: "Rigueur",              d: "Une méthode et une attention qui ne laissent pas les détails au hasard. Chaque élément convenu lors de votre évaluation est traité avec le même niveau de soin, à chaque visite.", core: true },
    { n: "02", t: "Constance",            d: "La qualité ne devrait pas dépendre de la journée. Notre objectif est de maintenir un niveau d'entretien stable et prévisible d'une visite à l'autre.", core: true },
    { n: "03", t: "Attention",            d: "Votre espace n'est pas un numéro. Nous adaptons notre approche aux priorités, aux surfaces, aux habitudes et aux besoins identifiés lors de l'évaluation." },
    { n: "04", t: "Contrôle qualité",     d: "Un standard qui se vérifie. Il fait partie de notre façon de travailler afin d'identifier ce qui peut être amélioré et maintenir le niveau attendu." },
    { n: "05", t: "Suivi",                d: "Une communication simple et professionnelle avant, pendant et après le service — pour les besoins, les changements et les demandes particulières." },
    { n: "06", t: "Tranquillité d'esprit", d: "Moins à gérer, plus de temps pour ce qui compte. L'entretien de votre espace devient une chose de moins à superviser au quotidien.", core: true },
];

const VERBS = [
    { slug: "emeraude", verb: "Préserver",   line: "Entretien résidentiel régulier." },
    { slug: "diamant",  verb: "Élever",      line: "Entretien résidentiel premium." },
    { slug: "rubis",    verb: "Transformer", line: "Grand ménage • Remise à niveau • Après construction." },
    { slug: "saphir",   verb: "Représenter", line: "Entretien commercial." },
    { slug: "perle",    verb: "Personnaliser", line: "Services complémentaires à la carte." },
];

export function BrandSignature({ className = "", color = "#E8D48B" }) {
    return (
        <div className={`inline-flex items-center gap-3 font-serif text-[11px] sm:text-[12px] tracking-[0.32em] uppercase ${className}`} style={{ color }}>
            <span>Rigueur</span>
            <span className="opacity-60">◆</span>
            <span>Constance</span>
            <span className="opacity-60">◆</span>
            <span>Tranquillité d'esprit</span>
        </div>
    );
}

export default function StandardKGD() {
    return (
        <section
            data-testid="standard-kgd"
            className="relative overflow-hidden py-20 lg:py-28"
            style={{ background: "linear-gradient(180deg,#041126 0%,#03102A 45%,#07204A 100%)" }}
            data-theme="dark"
        >
            {/* Halos */}
            <div className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(0,140,255,0.20), transparent 65%)" }}/>
            <div className="absolute -bottom-40 -right-40 w-[560px] h-[560px] rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(212,175,55,0.15), transparent 65%)" }}/>

            <div className="relative max-w-6xl mx-auto px-5 lg:px-8">
                {/* Header */}
                <div className="text-center">
                    <div className="eyebrow">Signature de la maison</div>
                    <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white mt-3 curve-divider leading-[1.08]">Le Standard KGD</h2>
                    <p className="mt-10 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed" style={{ color: "#E8EEF7" }}>
                        Plus qu'un espace propre. Une constance sur laquelle vous pouvez compter.
                    </p>
                    <p className="mt-4 max-w-3xl mx-auto text-sm sm:text-base leading-relaxed" style={{ color: "#CBD5E1" }}>
                        Chez Ménage KGD, la qualité ne repose pas uniquement sur ce qui est visible à la fin d'un entretien. Elle repose sur la manière dont chaque visite est préparée, exécutée et suivie.
                    </p>
                    <div className="mt-8 flex justify-center">
                        <BrandSignature/>
                    </div>
                </div>

                {/* 6 pillars */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 mt-14">
                    {PILLARS.map((p, i) => (
                        <motion.article
                            key={p.n}
                            initial={{ opacity: 0, y: 18 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-30px" }}
                            transition={{ duration: 0.6, delay: i * 0.05, ease: [0.2,0.7,0.2,1] }}
                            data-testid={`pillar-${p.t.toLowerCase().split(' ')[0]}`}
                            className="rounded-2xl p-6 sm:p-7 relative"
                            style={{
                                background: "linear-gradient(180deg, rgba(255,255,255,0.045) 0%, rgba(255,255,255,0.02) 100%)",
                                border: `1px solid ${p.core ? "rgba(212,175,55,0.45)" : "rgba(212,175,55,0.20)"}`,
                                boxShadow: "0 20px 40px -28px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.05)",
                                backdropFilter: "blur(14px)",
                            }}
                        >
                            <div className="flex items-baseline gap-3">
                                <span className="font-serif text-2xl" style={{ background: "linear-gradient(180deg,#E8D48B,#B8912B)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>{p.n}</span>
                                <span className="h-px flex-1" style={{ background: "linear-gradient(90deg, rgba(212,175,55,0.5), transparent)" }}/>
                                {p.core && <span className="text-[9px] uppercase tracking-widest" style={{ color: "#E8D48B" }}>Cœur KGD</span>}
                            </div>
                            <h3 className="font-serif text-xl sm:text-2xl text-white mt-3">{p.t}</h3>
                            <p className="text-sm mt-2 leading-relaxed" style={{ color: "#E8EEF7" }}>{p.d}</p>
                        </motion.article>
                    ))}
                </div>

                {/* Editorial: what you really choose */}
                <div className="mt-20 grid lg:grid-cols-[1fr_1.2fr] gap-10 items-start">
                    <div>
                        <div className="eyebrow">Ce que vous choisissez réellement</div>
                        <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-white mt-3 leading-[1.15]">La différence se trouve dans les détails.</h3>
                        <p className="mt-5 text-sm sm:text-base leading-relaxed" style={{ color: "#E8EEF7" }}>
                            Choisir Ménage KGD, ce n'est pas simplement réserver quelques heures de ménage. C'est choisir une méthode, une constance, une attention aux détails et une tranquillité d'esprit.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {[
                            { t: "Votre espace",       d: "Un entretien adapté à la réalité des lieux." },
                            { t: "Votre temps",        d: "Moins de gestion et moins de tâches à reprendre." },
                            { t: "Votre tranquillité", d: "Une équipe et une méthode sur lesquelles vous pouvez compter." },
                        ].map(x => (
                            <div key={x.t} className="rounded-xl p-5" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(212,175,55,0.25)" }}>
                                <div className="font-serif text-lg text-white">{x.t}</div>
                                <p className="text-xs sm:text-sm mt-1 leading-relaxed" style={{ color: "#CBD5E1" }}>{x.d}</p>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Subtle conceptual comparison */}
                <div className="mt-16 grid md:grid-cols-2 gap-4">
                    <div className="rounded-2xl p-6 sm:p-7" style={{ background: "rgba(255,255,255,0.02)", border: "1px dashed rgba(255,255,255,0.18)" }}>
                        <div className="text-[10px] uppercase tracking-widest" style={{ color: "#CBD5E1" }}>Un simple entretien</div>
                        <div className="font-serif text-xl text-white mt-2">Faire ce qui doit être nettoyé.</div>
                    </div>
                    <div className="rounded-2xl p-6 sm:p-7" style={{ background: "linear-gradient(180deg, rgba(212,175,55,0.10), rgba(0,140,255,0.04))", border: "1px solid rgba(212,175,55,0.4)" }}>
                        <div className="text-[10px] uppercase tracking-widest" style={{ background: "linear-gradient(90deg,#E8D48B,#D4AF37)", WebkitBackgroundClip: "text", backgroundClip: "text", color: "transparent" }}>Le Standard KGD</div>
                        <div className="font-serif text-xl text-white mt-2">Comprendre l'espace. Définir les priorités. Maintenir la constance. Adapter l'entretien. Assurer un suivi.</div>
                    </div>
                </div>

                {/* Transition: one standard, five collections */}
                <div className="mt-20 text-center">
                    <div className="eyebrow">Un Standard. Cinq Collections.</div>
                    <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl text-white mt-3 leading-[1.15] max-w-3xl mx-auto">Votre besoin détermine la Collection.<br className="hidden sm:block"/> Notre Standard, lui, ne change pas.</h3>
                </div>

                <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    {VERBS.map(v => {
                        const c = COLLECTIONS.find(x => x.slug === v.slug);
                        return (
                            <Link key={v.slug} to={`/collections/${v.slug}`} data-testid={`std-verb-${v.slug}`} className="group rounded-xl p-4 text-left transition-all hover:-translate-y-0.5" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${c.primary}40` }}>
                                <div className="text-[10px] uppercase tracking-widest" style={{ color: c.primary === "#7DD3FC" ? "#B8E6FF" : c.primary }}>{c.name}</div>
                                <div className="font-serif text-xl text-white mt-1">{v.verb}</div>
                                <div className="text-[11px] mt-1" style={{ color: "#CBD5E1" }}>{v.line}</div>
                                <div className="mt-3 inline-flex items-center gap-1 text-[11px]" style={{ color: "#E8D48B" }}>
                                    Découvrir <ChevronRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5"/>
                                </div>
                            </Link>
                        );
                    })}
                </div>

                <div className="mt-10 text-center">
                    <Link to="/collections" className="btn-ghost-gold" data-testid="std-collections-cta">Découvrir nos Collections</Link>
                </div>

                {/* Notre engagement */}
                <div className="mt-16 max-w-2xl mx-auto text-center">
                    <div className="eyebrow">Notre engagement</div>
                    <p className="mt-3 text-sm sm:text-base leading-relaxed" style={{ color: "#E8EEF7" }}>
                        Nous préférons offrir un nombre de services que nous sommes réellement capables de maintenir avec constance plutôt que de multiplier les rendez-vous au détriment de la qualité.
                    </p>
                    <p className="mt-3 text-xs sm:text-sm" style={{ color: "#CBD5E1" }}>
                        Les disponibilités peuvent donc varier selon le secteur, la fréquence et la Collection choisie.
                    </p>
                </div>
            </div>
        </section>
    );
}
